import Navbar from "../../shared/navbar";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { ALERT, BASE_URL } from "../../config/constants";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Swal from "sweetalert2";

const schemaRegister = yup.object().shape({
  name: yup
    .string()
    .max(25, "No debe exceder 25 caracteres")
    .required("Nombre es obligatorio")
    .matches(
      /^([a-zA-ZñÑ ]*)$/,
      "El nombre no debe tener caracteres especiales",
    ),
  lastName: yup
    .string()
    .max(30, "No debe exceder 30 caracteres")
    .required("Apellido es obligatorio")
    .matches(
      /^([a-zA-ZñÑ ]*)$/,
      "El apellido no debe tener caracteres especiales",
    ),
  ndip: yup
    .string()
    .max(20, "No debe exceder 20 caracteres")
    .required("El número de identificación es requerido")
    .matches(/^([0-9]*)$/, "Solo se permiten números")
    .typeError("El número no debe contener puntos ni comas"),
  email2: yup
    .string()
    .required("El correo es obligatorio")
    .email("El email no es válido, debe contener @")
    .typeError("El correo debe ser en formato example@email.com"),
  email: yup
    .string()
    .max(50, "No debe exceder 50 caracteres")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Dirección de correo inválida")
    .required("La confirmación de correo es requerida")
    .email("El email no es válido, debe contener @")
    .test("match", "Los correos electrónicos no coinciden", function (value) {
      return value === this.parent.email2;
    }),
  password: yup
    .string()
    .required("La contraseña es obligatoria")
    .matches(/[A-Z]/, "Debe contener al menos una letra mayúscula")
    .matches(
      /[!@#$%^&*()_+\-={};':"|,.<>?]/,
      "Debe contener al menos un carácter especial",
    )
    .notOneOf([
      "titan",
      "Titan",
      "Titan1234567",
      "Titan123456*",
      "Decoblock",
      "1234",
      "deco",
      "Deco",
    ])
    .test(
      "password",
      "Mínimo 9 caracteres, al menos un número, sin palabras clave",
      (value) => {
        return value && value.length >= 9 && /[0-9]/.test(value);
      },
    ),
  password2: yup
    .string()
    .required("Confirmación requerida")
    .test("password", "Las contraseñas no coinciden", function (value) {
      return value === this.parent.password;
    }),
  phone: yup
    .string()
    .max(20, "No debe exceder 20 caracteres")
    .required("El teléfono es obligatorio")
    .matches(/^([0-9]*)$/, "Solo se permiten números")
    .test(
      "len",
      "Debe contener al menos 10 dígitos",
      (value) => value && value.length >= 10,
    ),
});

// ── Tokens de estilo — exactamente los mismos que home.jsx ──────────────────
const inputStyle = (hasError) => ({
  backgroundColor: "rgba(255,255,255,0.05)",
  border: hasError
    ? "1px solid rgba(220,53,69,0.7)"
    : "1px solid rgba(255,255,255,0.15)",
  color: "#fff",
  padding: "0.72rem 0.9rem",
  borderRadius: "0.8rem",
  boxShadow: "none",
  fontSize: "0.95rem",
});

const labelStyle = {
  color: "rgba(255,255,255,0.78)",
  fontSize: "0.9rem",
  marginBottom: "0.5rem",
  display: "block",
};

const errorStyle = {
  color: "#ffb3b3",
  fontSize: "0.8rem",
  marginTop: "0.45rem",
};

// ── Separador de sección ─────────────────────────────────────────────────────
function SectionLabel({ text }) {
  return (
    <div className="d-flex align-items-center gap-2 mb-3 mt-1">
      <span
        style={{
          fontSize: "0.78rem",
          fontWeight: 600,
          color: "rgba(255,255,255,0.38)",
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </span>
      <div
        style={{
          flex: 1,
          height: 1,
          backgroundColor: "rgba(255,255,255,0.08)",
        }}
      />
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function Register() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schemaRegister),
  });

  function registrar(data, event) {
    event.preventDefault();
    data.name = data.name.toUpperCase().trim();
    data.lastName = data.lastName.toUpperCase().trim();

    axios
      .post(BASE_URL + "/usuarios", data)
      .then(() => {
        Swal.fire(
          `Bienvenido ${data.name} ${data.lastName}`,
          "Tu registro fue exitoso",
          "success",
        );
        reset();
        navigate("/login");
      })
      .catch((err) => {
        const error = err.response.data;
        ALERT.fire({
          icon: "error",
          title: "Error en el registro",
          text: error.msg,
        });
      });
  }

  return (
    <div
      className="min-vh-100 d-flex flex-column"
      style={{
        background: "linear-gradient(180deg, #050b18 0%, #081224 100%)",
      }}
    >
      <Navbar />

      <div className="container flex-grow-1 py-4 py-lg-5 d-flex align-items-center">
        <div className="row w-100 justify-content-center">
          <div className="col-12 col-lg-10 col-xl-9">
            {/* ── Card — mismo estilo que el login card de home.jsx ── */}
            <div
              className="rounded-4 shadow-lg overflow-hidden"
              style={{
                backgroundColor: "rgba(6, 20, 43, 0.95)",
                border: "1px solid rgba(255,255,255,0.10)",
                backdropFilter: "blur(6px)",
              }}
            >
              {/* Header */}
              <div
                className="px-4 px-md-5 pt-4 pb-3"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
              >
                {/* Badge — mismo patrón que home.jsx */}
                <div
                  className="d-inline-flex align-items-center gap-2 rounded-pill px-3 py-2 mb-3"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    fontSize: "0.8rem",
                    color: "rgba(255,255,255,0.75)",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#1450ff",
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  Portal unificado para Titán App y Extranet
                </div>

                <h1
                  className="fw-semibold text-white mb-1"
                  style={{
                    fontSize: "clamp(1.5rem, 3vw, 2rem)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.2,
                  }}
                >
                  Crear cuenta en{" "}
                  <span style={{ color: "#6aa1ff" }}>TITÁN</span>
                </h1>
                <p
                  className="mb-0"
                  style={{
                    color: "rgba(255,255,255,0.65)",
                    fontSize: "0.92rem",
                  }}
                >
                  Completa tus datos para registrarte en el sistema
                </p>
              </div>

              {/* Body */}
              <div className="px-4 px-md-5 py-4">
                <form onSubmit={handleSubmit(registrar)}>
                  {/* ── Información personal ── */}
                  <SectionLabel text="Información personal" />

                  <div className="row g-3 mb-2">
                    <div className="col-12 col-md-6">
                      <label style={labelStyle}>Nombres</label>
                      <input
                        type="text"
                        className="form-control"
                        style={inputStyle(!!errors.name)}
                        placeholder="Ej. Camilo"
                        {...register("name")}
                      />
                      {errors?.name?.message && (
                        <div style={errorStyle}>{errors.name.message}</div>
                      )}
                    </div>

                    <div className="col-12 col-md-6">
                      <label style={labelStyle}>Apellidos</label>
                      <input
                        type="text"
                        className="form-control"
                        style={inputStyle(!!errors.lastName)}
                        placeholder="Ej. Torres"
                        {...register("lastName")}
                      />
                      {errors?.lastName?.message && (
                        <div style={errorStyle}>{errors.lastName.message}</div>
                      )}
                    </div>

                    <div className="col-12 col-md-6">
                      <label style={labelStyle}>Tipo de documento</label>
                      <select
                        className="form-select"
                        style={inputStyle(false)}
                        {...register("docType")}
                      >
                        <option value="Cédula de ciudadanía">
                          Cédula de ciudadanía
                        </option>
                        <option value="Cédula de extranjería">
                          Cédula de extranjería
                        </option>
                        <option value="Pasaporte">Pasaporte</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label style={labelStyle}>Número de identificación</label>
                      <input
                        type="number"
                        step="1"
                        className="form-control"
                        style={inputStyle(!!errors.ndip)}
                        placeholder="Sin puntos ni comas"
                        {...register("ndip")}
                      />
                      {errors?.ndip?.message && (
                        <div style={errorStyle}>{errors.ndip.message}</div>
                      )}
                    </div>
                  </div>

                  {/* ── Contacto y acceso ── */}
                  <SectionLabel text="Contacto y acceso" />

                  <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6">
                      <label style={labelStyle}>Correo electrónico</label>
                      <input
                        type="email"
                        className="form-control"
                        style={inputStyle(!!errors.email2)}
                        placeholder="example@titancemento.com"
                        {...register("email2")}
                      />
                      {errors?.email2?.message && (
                        <div style={errorStyle}>{errors.email2.message}</div>
                      )}
                    </div>

                    <div className="col-12 col-md-6">
                      <label style={labelStyle}>Confirmar correo</label>
                      <input
                        type="email"
                        className="form-control"
                        style={inputStyle(!!errors.email)}
                        placeholder="Repite tu correo"
                        {...register("email")}
                      />
                      {errors?.email?.message && (
                        <div style={errorStyle}>{errors.email.message}</div>
                      )}
                    </div>

                    <div className="col-12 col-md-4">
                      <label style={labelStyle}>Contraseña</label>
                      <input
                        type="password"
                        className="form-control"
                        style={inputStyle(!!errors.password)}
                        placeholder="Mín. 12 caracteres"
                        {...register("password")}
                      />
                      {errors?.password?.message && (
                        <div style={errorStyle}>{errors.password.message}</div>
                      )}
                    </div>

                    <div className="col-12 col-md-4">
                      <label style={labelStyle}>Confirmar contraseña</label>
                      <input
                        type="password"
                        className="form-control"
                        style={inputStyle(!!errors.password2)}
                        placeholder="Repite tu contraseña"
                        {...register("password2")}
                      />
                      {errors?.password2?.message && (
                        <div style={errorStyle}>{errors.password2.message}</div>
                      )}
                    </div>

                    <div className="col-12 col-md-4">
                      <label style={labelStyle}>Teléfono</label>
                      <input
                        type="text"
                        className="form-control"
                        style={inputStyle(!!errors.phone)}
                        placeholder="Ej. 3001234567"
                        {...register("phone")}
                      />
                      {errors?.phone?.message && (
                        <div style={errorStyle}>{errors.phone.message}</div>
                      )}
                    </div>
                  </div>

                  {/* ── Botón — idéntico al de home.jsx ── */}
                  <div className="d-grid mb-3">
                    <button
                      type="submit"
                      className="btn fw-semibold"
                      style={{
                        backgroundColor: "#1450ff",
                        color: "#fff",
                        borderRadius: "0.8rem",
                        padding: "0.75rem 1rem",
                        border: "none",
                        boxShadow: "0 10px 24px rgba(20,80,255,0.20)",
                        fontSize: "0.95rem",
                      }}
                    >
                      Crear cuenta
                    </button>
                  </div>

                  {/* ── Links inferiores — mismos estilos que home.jsx ── */}
                  <div className="d-flex justify-content-between align-items-center">
                    <Link
                      to="/login"
                      style={{
                        color: "#9ec1ff",
                        textDecoration: "none",
                        fontSize: "0.86rem",
                      }}
                    >
                      ¿Ya tienes cuenta? Inicia sesión
                    </Link>

                    <span
                      style={{
                        color: "rgba(255,255,255,0.35)",
                        fontSize: "0.78rem",
                      }}
                    >
                      Portal unificado TITÁN
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
